import React, { useEffect, useState } from 'react';
import { ClockIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SessionExpirationModalProps {
  isOpen: boolean;
  sessionState: 'warning' | 'final-warning' | 'expired';
  timeRemaining: number; // in milliseconds
  onExtendSession: () => void;
  onSignOut: () => void;
  onClose: () => void;
  isExtending?: boolean;
}

export const SessionExpirationModal: React.FC<SessionExpirationModalProps> = ({
  isOpen,
  sessionState,
  timeRemaining,
  onExtendSession,
  onSignOut,
  onClose,
  isExtending = false,
}) => {
  const [countdown, setCountdown] = useState(timeRemaining);

  // Update countdown every second
  useEffect(() => {
    if (!isOpen || sessionState === 'expired') return;

    const interval = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, sessionState]);

  // Update countdown when timeRemaining changes
  useEffect(() => {
    setCountdown(timeRemaining);
  }, [timeRemaining]);

  if (!isOpen) return null;

  // Format time remaining
  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const getModalConfig = () => {
    switch (sessionState) {
      case 'warning':
        return {
          title: 'Session Expiring Soon',
          message: `Your session will expire in ${formatTime(countdown)}. Would you like to extend your session?`,
          icon: <ClockIcon className="h-6 w-6 text-yellow-600" />,
          iconBg: 'bg-yellow-100',
          primaryButton: {
            text: 'Extend Session',
            action: onExtendSession,
            class: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
          },
          secondaryButton: {
            text: 'Continue',
            action: onClose,
            class: 'bg-gray-300 hover:bg-gray-400 text-gray-800',
          },
          showClose: true,
        };
      case 'final-warning':
        return {
          title: 'Session Expiring Now!',
          message: `Your session will expire in ${formatTime(countdown)}. Extend now or you'll be signed out automatically.`,
          icon: <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />,
          iconBg: 'bg-red-100',
          primaryButton: {
            text: 'Extend Session',
            action: onExtendSession,
            class: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          },
          secondaryButton: {
            text: 'Sign Out Now',
            action: onSignOut,
            class: 'bg-gray-300 hover:bg-gray-400 text-gray-800',
          },
          showClose: false,
        };
      case 'expired':
        return {
          title: 'Session Expired',
          message: 'Your session has expired for security reasons. Please sign in again to continue.',
          icon: <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />,
          iconBg: 'bg-red-100',
          primaryButton: {
            text: 'Sign In Again',
            action: onSignOut,
            class: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
          },
          secondaryButton: null,
          showClose: false,
        };
    }
  };

  const config = getModalConfig();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
          onClick={config.showClose ? onClose : undefined}
        />

        {/* This element is to trick the browser into centering the modal contents. */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${config.iconBg} sm:mx-0 sm:h-10 sm:w-10`}>
                {config.icon}
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {config.title}
                  </h3>
                  {config.showClose && (
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  )}
                </div>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {config.message}
                  </p>
                  {sessionState !== 'expired' && countdown > 0 && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center justify-center">
                        <ClockIcon className="h-4 w-4 text-gray-500 mr-2" />
                        <span className="text-sm font-mono text-gray-700">
                          Time remaining: {formatTime(countdown)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              disabled={isExtending}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed ${config.primaryButton.class}`}
              onClick={config.primaryButton.action}
            >
              {isExtending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Extending...
                </>
              ) : (
                config.primaryButton.text
              )}
            </button>
            {config.secondaryButton && (
              <button
                type="button"
                className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm ${config.secondaryButton.class}`}
                onClick={config.secondaryButton.action}
              >
                {config.secondaryButton.text}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionExpirationModal;
